import { useEffect, useState } from "react";
import { Button, Flex, Modal, Spin, Tooltip, Typography } from "antd";
import { Icon } from "@iconify/react";

// Recognises the file from its first bytes instead of trusting the file name
// or the Content-Type the server sent, which are sometimes wrong (a PDF named
// without ".pdf", or "foto.JPG" served as application/octet-stream).
const detectFileType = (bytes) => {
  const startsWith = (...sig) => sig.every((b, i) => bytes[i] === b);
  if (startsWith(0x25, 0x50, 0x44, 0x46)) return { kind: "pdf", mime: "application/pdf" };
  if (startsWith(0x89, 0x50, 0x4e, 0x47)) return { kind: "image", mime: "image/png" };
  if (startsWith(0xff, 0xd8, 0xff)) return { kind: "image", mime: "image/jpeg" };
  if (startsWith(0x47, 0x49, 0x46, 0x38)) return { kind: "image", mime: "image/gif" };
  if (startsWith(0x42, 0x4d)) return { kind: "image", mime: "image/bmp" };
  if (
    startsWith(0x52, 0x49, 0x46, 0x46) &&
    bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50
  ) {
    return { kind: "image", mime: "image/webp" };
  }
  return null;
};

// Preview for documents and images, with a maximize toggle to view the file
// across the whole screen.
export default function DocumentPreviewModal({
  open,
  src,
  title = "Preview Dokumen",
  isPdf,
  onClose,
}) {
  const [maximized, setMaximized] = useState(false);
  const [preview, setPreview] = useState({ status: "idle" });

  useEffect(() => {
    if (!open) setMaximized(false);
  }, [open]);

  useEffect(() => {
    if (!open || !src) {
      setPreview({ status: "idle" });
      return undefined;
    }

    let cancelled = false;
    let createdUrl = null;
    setPreview({ status: "loading" });

    (async () => {
      try {
        const blob = await (await fetch(src)).blob();
        const header = new Uint8Array(await blob.slice(0, 12).arrayBuffer());
        const detected = detectFileType(header);
        if (cancelled) return;

        if (!detected) {
          // Unknown content: fall back to what the caller said it is.
          setPreview({
            status: "ready",
            kind: isPdf ? "pdf" : blob.type.startsWith("image/") ? "image" : "unknown",
            url: src,
          });
          return;
        }

        // Re-label the bytes so the browser's viewer accepts them.
        createdUrl = URL.createObjectURL(
          new Blob([blob], { type: detected.mime }),
        );
        setPreview({ status: "ready", kind: detected.kind, url: createdUrl });
      } catch {
        if (!cancelled) setPreview({ status: "error" });
      }
    })();

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [open, src, isPdf]);

  const renderContent = () => {
    if (preview.status === "loading" || preview.status === "idle") {
      return (
        <Flex align="center" justify="center" style={{ height: "100%" }}>
          <Spin />
        </Flex>
      );
    }
    if (preview.status === "error" || preview.kind === "unknown") {
      return (
        <Flex
          vertical
          align="center"
          justify="center"
          gap={8}
          style={{ height: "100%", textAlign: "center" }}
        >
          <Icon icon="solar:file-corrupted-line-duotone" width={40} height={40} />
          <Typography.Text type="secondary">
            {preview.status === "error"
              ? "Dokumen gagal dimuat."
              : "Format file ini tidak bisa ditampilkan. Silakan unduh dokumennya."}
          </Typography.Text>
        </Flex>
      );
    }
    if (preview.kind === "image") {
      return (
        <Flex
          align="center"
          justify="center"
          style={{ width: "100%", height: "100%" }}
        >
          <img
            src={preview.url}
            alt={title}
            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
          />
        </Flex>
      );
    }
    return (
      <embed
        src={preview.url}
        type="application/pdf"
        style={{ width: "100%", height: "100%", border: "none" }}
      />
    );
  };

  return (
    <Modal
      open={open}
      centered={!maximized}
      onCancel={onClose}
      title={
        <Flex align="center" justify="space-between" style={{ marginRight: 32 }}>
          <span>{title}</span>
          <Tooltip title={maximized ? "Perkecil" : "Perbesar"}>
            <Button
              type="text"
              size="small"
              aria-label={maximized ? "Perkecil preview" : "Perbesar preview"}
              icon={
                <Icon
                  icon={
                    maximized
                      ? "solar:minimize-square-linear"
                      : "solar:maximize-square-linear"
                  }
                  width={18}
                  height={18}
                />
              }
              onClick={() => setMaximized((prev) => !prev)}
            />
          </Tooltip>
        </Flex>
      }
      footer={[
        <Button key="close" onClick={onClose}>
          Tutup
        </Button>,
      ]}
      width={maximized ? "100vw" : 800}
      style={
        maximized
          ? { top: 0, margin: 0, padding: 0, maxWidth: "100vw" }
          : undefined
      }
      styles={{
        content: maximized
          ? {
              height: "100vh",
              borderRadius: 0,
              display: "flex",
              flexDirection: "column",
            }
          : undefined,
        body: maximized ? { flex: 1, minHeight: 0 } : { height: "70vh" },
      }}
    >
      {renderContent()}
    </Modal>
  );
}
